import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { Modal, Row, Col, Overlay, Popover, Button } from 'react-bootstrap';
import { NumericFormat as NumberFormat } from "react-number-format";

import Table from "../../../../../layout/table";
import Hamburger from "../../../../../layout/kebab";
import ConditionalButton from "../../../../../layout/conditional-button";

import { activity, get as getInvoice } from "../../../../../actions/invoices";
import { get as getClient } from "../../../../../actions/clients";
import { get as getLead } from "../../../../../actions/leads";
import { deepEqual } from "../../../../../utils/object";
import { format as formatDate } from "../../../../../utils/date";
import { currency } from "../../../../../utils/number";

class BillingSelect extends React.Component {
    /**
     *
     */
    constructor(props) {
        super(props);

        this.state = {
            pages: 0,
            data: [],
            credits: [],
            debits: [],
            index: 0,
            direction: "DESC",
            size: 10,
            menu: {},
            exclusions: [],
            ...this.mapPropsToState(props)
        }
        
    }

    mapPropsToState = (props) => {
        const {
            personUuid,
            show,
            size,
            page
        } = props;

        return {
            personUuid,
            show,
            size: Math.max(1, parseInt(size ?? "10", 10) || 10),
            page: Math.max(0, parseInt(page ?? "0", 10) || 0)
        }
    };

    getInitialState = (props = this.props) => ({
        pages: 0,
        data: [],
        credits: [],
        debits: [],
        index: 0,
        direction: "DESC",
        size: 10,
        menu: {},
        exclusions: [],
        calculations: { clocks: 0, expenses: 0, payments: 0 },
        latest: null,
        retainer: 0,
        applied: 0,
        due: 0,
        ...this.mapPropsToState(props),
    });

    resetState = (cb) => {
        // important: also clear the "loading" guard so loadData can run cleanly next open
        this.loading = false;
        this.setState(this.getInitialState(), cb);
    };

    componentDidUpdate = async (oldProps) => {
        const oldSlice = this.mapPropsToState(oldProps);
        const newSlice = this.mapPropsToState(this.props);
        if (deepEqual(oldSlice, newSlice)) return;
        if (this.loading) return;
        
        this.setState(newSlice, this.loadData);
    }

    componentDidMount = async () => {
        await this.loadData();
    }

    loadData = async () => {
        if (!this.props.show) return;
        this.loading = true;

        const { personUuid } = this.state;
        let retainer = 0;


        const activity = await this.props.activity({
            personUuid,
            unbilled: true
        });

        const latest = await this.props.getInvoice({
            personUuid: personUuid,
            latest: true
        });

        const client = await this.props.getClient({ uuid: personUuid });
        if (client?.retainer) retainer = client?.retainer;

        if (!client?.retainer) {
            const lead = await this.props.getLead({ uuid: personUuid });
            if (lead?.retainer) retainer = lead?.retainer;
        }

        this.setState((prev) => {
            const excluded = new Set(prev.exclusions ?? []);

            const acc0 = {
                credits: [],
                debits: [],
                clockDebitSum: 0,
                expenseDebitSum: 0,
                paymentCreditSum: 0,
            };

            const { credits, debits, clockDebitSum, expenseDebitSum, paymentCreditSum } =
                (activity?.items ?? []).reduce((acc, x) => {
                    if (excluded.has(x?.uuid)) return acc;

                    const signed = Number(x?.signed) || 0;
                    const type = x?.type;

                    if (signed <= 0) {
                        acc.credits.push(x);

                        // payments are typically negative/credit
                        if (type === "PAYMENT") {
                            acc.paymentCreditSum += Math.abs(signed);
                        }
                    } else {
                        acc.debits.push(x);

                        if (type === "CLOCK") acc.clockDebitSum += signed;
                        if (type === "EXPENSE") acc.expenseDebitSum += signed;
                    }

                    return acc;
                }, acc0);

            // --- compute derived totals LOCALLY (don’t use this.subtotal/this.remaining here) ---
            const latestTotalBalance = Number(latest?.totalBalance) || 0;
            const latestTrustEnd = Number(latest?.trustEnd) || 0;

            const nextSubtotal =
                latestTotalBalance +
                clockDebitSum +
                expenseDebitSum;

            const nextRemaining =
                latestTrustEnd +
                paymentCreditSum -
                nextSubtotal;

            const nextApplied = nextSubtotal;          // or clamp to trust, etc., if that’s your rule
            const nextDue = retainer - nextRemaining;  // matches your current formula

            return {
                credits,
                debits,
                latest,

                pages: Math.ceil(debits.length / Number(prev.size)),
                data: debits.slice(0, prev.size),
                index: 0,
                direction: "DESC",
                page: 0,

                retainer,

                // keep these in sync with the newly computed totals
                applied: nextApplied,
                due: nextDue,

                calculations: {
                    ...prev.calculations,
                    clocks: clockDebitSum,
                    expenses: expenseDebitSum,
                    payments: paymentCreditSum,
                },
            };
        });

        this.loading = false;
    };

    onOk = () => {
        const exclusions = this.state.exclusions ?? [];
        const hasExclusions = exclusions.length > 0;
        const remaining = hasExclusions
            ? Array.from(
                new Set(
                    [
                        ...(this.state.debits ?? []),
                        ...(this.state.credits ?? []),
                    ]
                        .filter((x) => x?.uuid && !exclusions.includes(x.uuid))
                        .map((x) => x.uuid)
                )
            )
            : null;

        if (this.props.onOk) {
            this.props.onOk({
                applied: Number(this.state?.applied) || 0,
                payment: Number(this.state?.due) || 0,
                remaining, // null OR [uuid, uuid, ...]
            });
        }
    };


    onCancel = (e) => {
        this.resetState(() => {
            if (this.props.onCancel) this.props.onCancel(e);
        });
    };

    onHide = (e) => {
        this.onCancel(e);
    }

    onItemClick = (row) => {
        if (this.props.onItemClick) {
            this.props.onItemClick(row);
        }
    }
    
    openHamburger = (event, row) => {
        event.preventDefault();
        this.setState({
            menu: {
                show: true,
                row,
                target: event.currentTarget
            }
        });
    }
        
    closeHamburger = () => {
        this.setState(prev => ({
            menu: {
                ...prev.menu,
                show: false,
                target: null
            }
        }));
    }
        
    onActionFormat = (e, row) => {
        return (<>
            <Hamburger variant="link" vertical={false} onClick={(e) => this.openHamburger(e, row)} />
        </>)
    }

    onLinkFormat = (e, row) => {
        return (
            <Button variant="link" onClick={() => this.onItemClick(row)}>{e}</Button>
        )
    };

    onDateFormat = (e, row) => {
        return this.onLinkFormat(formatDate(e, false), row);
    }

    onCurrencyFormat = (e, row) => {
        const value = Math.abs(parseFloat(e));
        return this.onLinkFormat(currency(value), row);
    }

    onColumnClick = (e) => {
        const headers = this.headers;

        const nextIndex = headers.findIndex(
            (h) => h.header === e.header && h.source === e.source
        );

        const sortKey = e.source;
        const sortAs = (e.sortAs ?? "string").toLowerCase(); // default = string

        this.setState((prev) => {
            const sameCol = prev.index === nextIndex;
            const nextDirection = sameCol
                ? (prev.direction === "DESC" ? "ASC" : "DESC")
                : "DESC";

            const dirMul = nextDirection === "ASC" ? 1 : -1;

            const toFloat = (v) => {
                if (v == null) return Number.NaN;
                if (typeof v === "number") return v;
                // strip commas etc.
                const n = parseFloat(String(v).replace(/,/g, ""));
                return Number.isFinite(n) ? n : Number.NaN;
            };

            const toTime = (v) => {
                if (v == null) return Number.NaN;
                if (v instanceof Date) return v.getTime();
                const t = Date.parse(String(v));
                return Number.isFinite(t) ? t : Number.NaN;
            };

            const toString = (v) => (v == null ? "" : String(v));

            const compare = (a, b) => {
                const va = a?.[sortKey];
                const vb = b?.[sortKey];

                // keep null/undefined at the bottom regardless of direction
                const aNull = va == null || va === "";
                const bNull = vb == null || vb === "";
                if (aNull && bNull) return 0;
                if (aNull) return 1;
                if (bNull) return -1;

                if (sortAs === "float") {
                    const na = toFloat(va);
                    const nb = toFloat(vb);

                    // non-numbers go to the bottom
                    const aBad = !Number.isFinite(na);
                    const bBad = !Number.isFinite(nb);
                    if (aBad && bBad) return 0;
                    if (aBad) return 1;
                    if (bBad) return -1;

                    return (na - nb) * dirMul;
                }

                if (sortAs === "date") {
                    const ta = toTime(va);
                    const tb = toTime(vb);

                    // unparseable dates go to the bottom
                    const aBad = !Number.isFinite(ta);
                    const bBad = !Number.isFinite(tb);
                    if (aBad && bBad) return 0;
                    if (aBad) return 1;
                    if (bBad) return -1;

                    return (ta - tb) * dirMul;
                }

                // string (default)
                const sa = toString(va);
                const sb = toString(vb);
                return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" }) * dirMul;
            };

            const sortedDebits = [...prev.debits].sort(compare);

            return {
                debits: sortedDebits, // optional, but keeps state consistent with displayed data
                data: sortedDebits.slice(0, prev.size),
                index: nextIndex,
                direction: nextDirection,
                page: 0,
            };
        });
    };

    onPageChange = (e) => {
        this.setState({
            data: this.state.debits.slice(e * this.state.size, (e + 1) * this.state.size),
            page: parseInt(e)
        });
    }

    onRemoveTime = () => {
        this.setState((prev) => {
            const exclusions = prev.exclusions ?? [];
            const credits = prev.credits ?? [];
            const debits = prev.debits ?? [];
            const size = Number(prev.size) || 10;

            // find all CLOCK debits we’re removing
            const removedClockUuids = debits
                .filter((d) => d?.type === "CLOCK")
                .map((d) => d?.uuid)
                .filter(Boolean);

            if (!removedClockUuids.length) {
                // nothing to do, but close the menu if it’s open
                return {
                    menu: { ...prev.menu, show: false, target: null, row: null },
                };
            }

            const removedSet = new Set(removedClockUuids);

            // add to exclusions (dedupe)
            const nextExclusions = Array.from(new Set([...exclusions, ...removedClockUuids]));

            // remove CLOCK items from debits
            const nextDebits = debits.filter((d) => !removedSet.has(d?.uuid));

            // credits remain as-is (but safe to keep in sync pattern)
            const nextCredits = credits;

            // --- recompute calculations from what remains ---
            let clockDebitSum = 0;
            let expenseDebitSum = 0;

            for (const x of nextDebits) {
                const signed = Number(x?.signed) || 0;
                if (signed > 0) {
                    if (x?.type === "CLOCK") clockDebitSum += signed;     // should now be 0
                    if (x?.type === "EXPENSE") expenseDebitSum += signed;
                }
            }

            // payments are credits
            let paymentCreditSum = 0;
            for (const x of nextCredits) {
                const signed = Number(x?.signed) || 0;
                if (signed <= 0 && x?.type === "PAYMENT") paymentCreditSum += Math.abs(signed);
            }

            const latestTotalBalance = Number(prev.latest?.totalBalance) || 0;
            const latestTrustEnd = Number(prev.latest?.trustEnd) || 0;

            const nextSubtotal =
                latestTotalBalance +
                clockDebitSum +
                expenseDebitSum;

            const nextRemaining =
                latestTrustEnd +
                paymentCreditSum -
                nextSubtotal;

            const retainer = Number(prev.retainer) || 0;

            const pages = Math.ceil(nextDebits.length / size);

            return {
                exclusions: nextExclusions,
                credits: nextCredits,
                debits: nextDebits,

                pages,
                page: 0,
                data: nextDebits.slice(0, size),

                calculations: {
                    ...prev.calculations,
                    clocks: clockDebitSum,
                    expenses: expenseDebitSum,
                    payments: paymentCreditSum,
                },

                applied: nextSubtotal,
                due: retainer - nextRemaining,

                menu: { ...prev.menu, show: false, target: null, row: null },
            };
        });
    };

    onRemoveExpense = () => {
        this.setState((prev) => {
            const exclusions = prev.exclusions ?? [];
            const credits = prev.credits ?? [];
            const debits = prev.debits ?? [];
            const size = Number(prev.size) || 10;

            // find all EXPENSE debits we’re removing
            const removedExpenseUuids = debits
                .filter((d) => d?.type === "EXPENSE")
                .map((d) => d?.uuid)
                .filter(Boolean);

            if (!removedExpenseUuids.length) {
                return {
                    menu: { ...prev.menu, show: false, target: null, row: null },
                };
            }

            const removedSet = new Set(removedExpenseUuids);

            // add to exclusions (dedupe)
            const nextExclusions = Array.from(new Set([...exclusions, ...removedExpenseUuids]));

            // remove EXPENSE items from debits
            const nextDebits = debits.filter((d) => !removedSet.has(d?.uuid));

            // credits unchanged
            const nextCredits = credits;

            // --- recompute calculations from what remains ---
            let clockDebitSum = 0;
            let expenseDebitSum = 0;

            for (const x of nextDebits) {
                const signed = Number(x?.signed) || 0;
                if (signed > 0) {
                    if (x?.type === "CLOCK") clockDebitSum += signed;
                    if (x?.type === "EXPENSE") expenseDebitSum += signed; // should now be 0
                }
            }

            // payments are credits
            let paymentCreditSum = 0;
            for (const x of nextCredits) {
                const signed = Number(x?.signed) || 0;
                if (signed <= 0 && x?.type === "PAYMENT") paymentCreditSum += Math.abs(signed);
            }

            const latestTotalBalance = Number(prev.latest?.totalBalance) || 0;
            const latestTrustEnd = Number(prev.latest?.trustEnd) || 0;

            const nextSubtotal =
                latestTotalBalance +
                clockDebitSum +
                expenseDebitSum;

            const nextRemaining =
                latestTrustEnd +
                paymentCreditSum -
                nextSubtotal;

            const retainer = Number(prev.retainer) || 0;

            const pages = Math.ceil(nextDebits.length / size);

            return {
                exclusions: nextExclusions,
                credits: nextCredits,
                debits: nextDebits,

                pages,
                page: 0,
                data: nextDebits.slice(0, size),

                calculations: {
                    ...prev.calculations,
                    clocks: clockDebitSum,
                    expenses: expenseDebitSum,
                    payments: paymentCreditSum,
                },

                applied: nextSubtotal,
                due: retainer - nextRemaining,

                menu: { ...prev.menu, show: false, target: null, row: null },
            };
        });
    };

    onRemoveClick = (row) => {
        const uuid = row?.uuid;
        if (!uuid) return;

        this.setState((prev) => {
            const exclusions = prev.exclusions ?? [];
            const credits = prev.credits ?? [];
            const debits = prev.debits ?? [];
            const size = Number(prev.size) || 10;

            const nextExclusions = exclusions.includes(uuid)
                ? exclusions
                : [...exclusions, uuid];

            // remove from both arrays just in case (safe even if it only ever appears in debits)
            const nextDebits = debits.filter((d) => d?.uuid !== uuid);
            const nextCredits = credits.filter((c) => c?.uuid !== uuid);

            // --- recompute calculations from what remains ---
            let clockDebitSum = 0;
            let expenseDebitSum = 0;

            for (const x of nextDebits) {
                const signed = Number(x?.signed) || 0;
                const type = x?.type;
                if (signed > 0) {
                    if (type === "CLOCK") clockDebitSum += signed;
                    if (type === "EXPENSE") expenseDebitSum += signed;
                }
            }

            // payments are credits; keep whatever you already had unless you also allow removing credits
            // (if you DO allow removing credits, this recomputes PAYMENT credits from nextCredits)
            let paymentCreditSum = 0;
            for (const x of nextCredits) {
                const signed = Number(x?.signed) || 0;
                if (signed <= 0 && x?.type === "PAYMENT") paymentCreditSum += Math.abs(signed);
            }

            const latestTotalBalance = Number(prev.latest?.totalBalance) || 0;
            const latestTrustEnd = Number(prev.latest?.trustEnd) || 0;

            const nextSubtotal =
                latestTotalBalance +
                clockDebitSum +
                expenseDebitSum;

            const nextRemaining =
                latestTrustEnd +
                paymentCreditSum -
                nextSubtotal;

            const retainer = Number(prev.retainer) || 0;

            const pages = Math.ceil(nextDebits.length / size);

            return {
                exclusions: nextExclusions,
                credits: nextCredits,
                debits: nextDebits,

                pages,
                page: 0,
                data: nextDebits.slice(0, size),

                // keep your summary state in sync
                calculations: {
                    ...prev.calculations,
                    clocks: clockDebitSum,
                    expenses: expenseDebitSum,
                    payments: paymentCreditSum,
                },

                // if you want these fields to “track” the math, keep them updated here:
                applied: nextSubtotal,
                due: retainer - nextRemaining,

                // close the menu
                menu: { ...prev.menu, show: false, target: null, row: null },
            };
        });
    };


    get headers() {
        return [
            {
                header: "Date",
                sortable: true,
                source: "activityDate",
                className: "text-muted text-small text-uppercase w-10",
                sortAs: "date",
                onRender: this.onDateFormat
            },
            {
                header: "Description",
                sortable: true,
                source: "memo",
                className: "text-muted text-small text-uppercase w-60",
                onRender: this.onLinkFormat
            },
            {
                header: "Gross",
                sortable: true,
                source: "gross",
                className: "text-muted text-small text-uppercase w-10",
                sortAs: "float",
                onRender: this.onCurrencyFormat
            },
            {
                header: "-Adj",
                sortable: true,
                source: "writeOff",
                sortAs: "float",
                className: "text-muted text-small text-uppercase w-10",
                onRender: this.onCurrencyFormat
            },
            {
                header: "=Net",
                sortable: true,
                source: "signed",
                sortAs: "float",
                className: "text-muted text-small text-uppercase w-10",
                onRender: this.onCurrencyFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                onRender: this.onActionFormat
            }
        ]
    }

    get subtotal() {
        return (Number(this.state.latest?.totalBalance) || 0)
            + (Number(this.state.calculations?.clocks) || 0)
            + (Number(this.state.calculations?.expenses) || 0);
    };

    get remaining() {
        return (Number(this.state.latest?.trustEnd) || 0)
            + (Number(this.state.calculations?.payments) || 0)
            - this.state.applied;
    };

    onAppliedChange(value) {

        this.setState(prev => ({
            applied: value,
            due: prev.retainer-(Number(prev.latest?.trustEnd) || 0)
            - (Number(prev.calculations?.payments) || 0)
            - value
        }))
    }

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        })
    }

    render() {
        return (
            <>
                <Modal size="xl" backdrop="static" keyboard show={this.props.show} onHide={this.onHide}>
                    <Modal.Header closeButton>
                        <Modal.Title id="staticBackdropLabel">{this.props.title || `New Invoice`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ paddingTop: "0px" }}>
                        <Row className="g-3 pb-3 mt-1">
                            <Col xs="12">
                                <Table
                                    headers={this.headers}
                                    page={this.state.data}
                                    sort={{ index: this.state.index, direction: this.state.direction }}
                                    size={this.state.size}
                                    index={this.state.page}
                                    pages={this.state.pages}
                                    onColumnClick={this.onColumnClick}
                                    onPageChange={this.onPageChange}
                                />
                            </Col>
                            <Col xs="6" md="7">
                            </Col>
                            <Col xs="6" md="5">
                                <Row>
                                    <Col xs="8" className="text-end">+ Previous Balance</Col>
                                    <Col xs="4" className="text-end"><p style={{ marginRight: "20px" }}>{currency(this.state.latest?.totalBalance)}</p></Col>
                                    <Col xs="8" className="text-end">+ Services</Col>
                                    <Col xs="4" className="text-end"><p style={{ marginRight: "20px" }}>{currency(this.state.calculations?.clocks)}</p></Col>
                                    <Col xs="8" className="text-end">+ Expenses</Col>
                                    <Col xs="4" className="text-end"><p style={{ marginRight: "20px" }}>{currency(this.state.calculations?.expenses)}</p></Col>
                                    <Col xs="8" className="text-end">Subtotal</Col>
                                    <Col xs="4" className="text-end"><p style={{ marginRight: "20px" }}>{currency(this.subtotal)}</p></Col>
                                    <Col xs="8" className="text-end">- Payments</Col>
                                    <Col xs="4" className="text-end"><p style={{ marginRight: "20px" }}>{currency(this.state?.calculations?.payments)}</p></Col>
                                    <Col xs="8" className="text-end">- Trust Applied</Col>
                                    <Col xs="4">
                                        <NumberFormat
                                                displayType="input"
                                                type="text"
                                                id="applied"
                                                name="applied"
                                                className="text-end form-control no-outline"
                                                style={{outline: "none !important", boxShadow: "none !important", position: "relative", top: "-8px"}}
                                                prefix={"$"}
                                                thousandSeparator={true}
                                                allowNegative={false}
                                                value={this.state?.applied}
                                                onValueChange={(e) =>
                                                    this.onAppliedChange(e.floatValue)
                                                }
                                                removeFormatting
                                            />
                                    </Col>
                                    <Col xs="8" className="text-end">Trust Remaining</Col>
                                    <Col xs="4" className="text-end"><p style={{ marginRight: "20px" }}>{currency(this.remaining)}</p></Col>
                                    
                                    <Col xs="8" className="text-end">Minimum Payment Due</Col>
                                    <Col xs="4">
                                        <NumberFormat
                                                displayType="input"
                                                type="text"
                                                id="due"
                                                name="due"
                                                className="text-end form-control no-outline"
                                                style={{outline: "none !important", boxShadow: "none !important", position: "relative", top: "-6px"}}
                                                prefix={"$"}
                                                thousandSeparator={true}
                                                allowNegative={false}
                                                value={this.state?.due}
                                                onValueChange={(e) =>
                                                    this.onChange({target: {
                                                        name: "due",
                                                        value: e.floatValue
                                                    }})
                                                }
                                                removeFormatting
                                            />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onRemoveExpense} icon="minus">remove expenses</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onRemoveTime} icon="minus">remove time</ConditionalButton>
                        
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onCancel} icon="error-hexagon">cancel</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" disabledVariant="outline-light" display enabled onEnabledClick={this.onOk} icon="save">ok</ConditionalButton>
                    </Modal.Footer>
                </Modal>

                <Overlay
                    target={this.state.menu.target}
                    show={this.state.menu.show}
                    placement="bottom-end"
                    rootClose
                    onHide={this.closeHamburger}
                >
                    {(props) => (
                        <Popover {...props}>
                                           
                            <Popover.Body>
                                <Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => this.onItemClick(this.state.menu.row)}
                                        >
                                            Open
                                        </button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => this.onRemoveClick(this.state.menu.row)}
                                        >
                                            Remove
                                        </button>
                                    </Col>
                                </Row>
                            </Popover.Body>
                        </Popover>
                    )}
                </Overlay>
            </>
        )
    }
}

BillingSelect.propTypes = {
    auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default connect(
    mapStateToProps,
    {
        activity,
        getInvoice,
        getClient,
        getLead
    },
    null,
    {
        forwardRef: true
    }
)(BillingSelect);
